import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/components/nutrimart/types";

interface Tx {
  id: string;
  order_id: string;
  amount: number;
  currency: string;
  status: string;
  provider_ref: string | null;
  card_last4: string | null;
  card_brand: string | null;
  parent_transaction_id: string | null;
  created_at: string;
  error_message: string | null;
}

export function AzulTransactionsAdmin({ lang }: { lang: Lang }) {
  const [rows, setRows] = useState<Tx[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const t = lang === "es" ? {
    title: "Transacciones AZUL", empty: "No hay transacciones registradas.",
    void: "Anular", refund: "Reembolsar", confirmVoid: "¿Anular esta transacción?",
    confirmRefund: "¿Reembolsar el monto total?", refresh: "Actualizar",
  } : {
    title: "AZUL Transactions", empty: "No transactions recorded yet.",
    void: "Void", refund: "Refund", confirmVoid: "Void this transaction?",
    confirmRefund: "Refund the full amount?", refresh: "Refresh",
  };

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("payment_transactions")
      .select("id, order_id, amount, currency, status, provider_ref, card_last4, card_brand, parent_transaction_id, created_at, error_message")
      .eq("provider", "azul")
      .order("created_at", { ascending: false })
      .limit(100);
    setRows((data as Tx[]) ?? []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function action(fn: "azul-void" | "azul-refund", tx: Tx) {
    const ok = confirm(fn === "azul-void" ? t.confirmVoid : t.confirmRefund);
    if (!ok) return;
    setBusy(tx.id);
    const body = fn === "azul-void"
      ? { transaction_id: tx.id }
      : { transaction_id: tx.id, amount_dop: tx.amount };
    const { data, error } = await supabase.functions.invoke(fn, { body });
    setBusy(null);
    if (error || !data?.approved) {
      alert(`❌ ${error?.message || data?.message || data?.error || "Falló"}`);
    } else {
      alert("✅ OK");
      load();
    }
  }

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h4 style={{ margin: 0 }}>{t.title}</h4>
        <button className="btn bo bs" onClick={load} disabled={loading}>↻ {t.refresh}</button>
      </div>
      {rows.length === 0 ? (
        <div style={{ color: "var(--tx3)", fontSize: 12 }}>{t.empty}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--bg2)", textAlign: "left" }}>
                <th style={{ padding: 6 }}>Fecha</th>
                <th style={{ padding: 6 }}>Order</th>
                <th style={{ padding: 6 }}>Monto</th>
                <th style={{ padding: 6 }}>Tarjeta</th>
                <th style={{ padding: 6 }}>AzulRef</th>
                <th style={{ padding: 6 }}>Estado</th>
                <th style={{ padding: 6 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tx) => (
                <tr key={tx.id} style={{ borderBottom: "1px solid var(--bd2)" }}>
                  <td style={{ padding: 6 }}>{new Date(tx.created_at).toLocaleString()}</td>
                  <td style={{ padding: 6 }}>{tx.order_id.slice(0, 8)}</td>
                  <td style={{ padding: 6 }}>{tx.currency} {Number(tx.amount).toFixed(2)}</td>
                  <td style={{ padding: 6 }}>{tx.card_brand ?? "—"} ····{tx.card_last4 ?? ""}</td>
                  <td style={{ padding: 6 }}>{tx.provider_ref ?? "—"}</td>
                  <td style={{ padding: 6 }}>
                    <span style={{
                      padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                      background: tx.status === "captured" ? "#DCFCE7" : tx.status === "failed" ? "#FEE2E2" : "#F3F4F6",
                      color: tx.status === "captured" ? "#166534" : tx.status === "failed" ? "#991B1B" : "#374151",
                    }}>{tx.status}</span>
                  </td>
                  <td style={{ padding: 6, display: "flex", gap: 4 }}>
                    {tx.status === "captured" && !tx.parent_transaction_id && (
                      <>
                        <button className="btn bo bs" disabled={busy === tx.id}
                          onClick={() => action("azul-void", tx)}>{t.void}</button>
                        <button className="btn bo bs" disabled={busy === tx.id}
                          onClick={() => action("azul-refund", tx)}>{t.refund}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}